<template>
	<div class="key-dates-content">
		<div v-if="keyDateCount > 0">
			<div v-for="(row, index) in keyDates" :key="index" class="key-dates-section">
				<div v-for="(lease, leaseIndex) in row.leases" :key="leaseIndex" class="key-dates-lease">
					<!-- Site and Lease Header -->
					<div class="key-dates-header">
						<div class="key-dates-site">
							<img 
								v-if="row.site.image && row.site.image.view_url" 
								:src="row.site.image.view_url" 
								:alt="row.site.building.address_line_1"
								class="key-dates-site-image"
							/>
							<div class="key-dates-site-info">
								<a :href="row.site.view_url" class="projects-table-content__link">
									{{ row.site.building.address_line_1 }}
								</a>
								<div class="key-dates-site-location">
									{{ row.site.building.city }}, {{ row.site.building.state }}
								</div>
							</div>
						</div>
						<div class="key-dates-lease-name">{{ lease.name }}</div>
					</div>

					<!-- Key Dates Table -->
					<themed-table
						v-if="lease.dates && lease.dates.length"
						:data="lease.dates"
						:columns="tableColumns"
						:hoverable="true"
						aria-label="Key Dates Table"
					>
						<!-- Status with conditional badges -->
						<template #column-status="{ row }">
							<span v-if="!row.completed_at" class="projects-table-content__badge projects-table-content__badge--red">
								INCOMPLETE
							</span>
							<span v-else class="projects-table-content__badge projects-table-content__badge--green">
								COMPLETED
							</span>
						</template>
						
						<!-- Note with icon -->
						<template #column-note="{ row }">
							<span v-if="row.note" :title="row.note" style="cursor: help;">
								<svg width="20" height="20" fill="currentColor" style="color: var(--muted-foreground);" viewBox="0 0 24 24">
									<path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-7h8v2H8v-2zm0 4h8v2H8v-2zm0-8h5v2H8V9z"/>
								</svg>
							</span>
						</template>
					</themed-table>
					<div v-else class="projects-table__placeholder">
						No Key Dates Found for this lease
					</div>
				</div>
			</div>
		</div>
		<div v-else class="projects-table__placeholder">
			No Key Dates Found
		</div>
	</div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';

export default {
	name: 'KeyDatesTableContent',
	components: {
		ThemedTable
	},
	props: {
		sites: {
			type: Array,
			required: true
		}
	},
	computed: {
		tableColumns() {
			return [
				{
					field: 'lease_clause',
					label: 'CLAUSE',
					type: 'text',
					sortable: true,
					searchable: true
				},
				{
					field: 'occurs_on',
					label: 'OCCURS ON',
					type: 'text',
					sortable: true,
					formatter: this.formatDate
				},
				{
					field: 'type',
					label: 'TYPE',
					type: 'text',
					sortable: true,
					searchable: true
				},
				{
					field: 'status',
					label: 'STATUS',
					type: 'text',
					sortable: false
				},
				{
					field: 'note',
					label: 'NOTE',
					type: 'text',
					sortable: false
				}
			];
		},
		activeSites() {
			return this.sites.filter(site => !site.archived_at);
		},
		keyDates() {
			return this.activeSites.map(site => {
				return {
					site: site,
					leases: (site.leases || []).map(lease => {
						return {
							name: lease.name,
							dates: lease.key_dates || []
						};
					})
				};
			}).filter(row => row.leases.length > 0);
		},
		keyDateCount() {
			return this.keyDates.reduce((total, row) => {
				return total + row.leases.reduce((leaseTotal, lease) => {
					return leaseTotal + (lease.dates?.length || 0);
				}, 0);
			}, 0);
		}
	},
	methods: {
		formatDate(date) {
			if (!date) return '-';
			const d = new Date(date);
			return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
		}
	}
}
</script>

<style scoped>

.key-dates-section {
	margin-bottom: 2rem;
}

.key-dates-lease {
	margin-bottom: 2.5rem;
	padding: 1.5rem;
	background: var(--card);
	border: 1px solid var(--border);
	border-radius: 0.5rem;
}

.key-dates-header {
	display: flex;
	align-items: center;
	gap: 1.5rem;
	margin: -1.5rem -1.5rem 1rem -1.5rem;
	padding: 1rem 1.5rem;
	border-bottom: 1px dashed var(--border);
}

.key-dates-site {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding-right: 1.5rem;
	border-right: 1px dashed var(--border);
}

.key-dates-site-image {
	width: 40px;
	height: 40px;
	border-radius: 0.375rem;
	object-fit: cover;
}

.key-dates-site-info {
	display: flex;
	flex-direction: column;
}

.key-dates-site-location {
	font-size: 0.875rem;
	color: var(--muted-foreground);
	margin-top: 0.125rem;
}

.key-dates-lease-name {
	font-size: 1.125rem;
	font-weight: 500;
	color: var(--foreground);
}
</style>
